import { useEffect, useState, useRef } from 'react'
import { useWallets } from '@privy-io/react-auth'

interface CoinbaseOnrampProps {
  onSuccess: (txHash: string) => void
  onError: (error: string) => void
  onClose: () => void
  amount: number // Prize pool amount in USD
}

// Global flag to prevent multiple instances from initializing simultaneously
let globalInitializing = false
let globalOnrampUrl: string | null = null
let globalPopupWindow: Window | null = null // Track the single global popup window

export function CoinbaseOnramp({ onSuccess, onError, onClose, amount }: CoinbaseOnrampProps) {
  const { wallets } = useWallets()
  const [isLoading, setIsLoading] = useState(true)
  const popupRef = useRef<Window | null>(null)
  const hasInitialized = useRef(false)

  // Reusable function to open popup with a given URL
  const openPopupWithUrl = (onrampUrl: string) => {
    // Check if there's already a global popup open
    if (globalPopupWindow && !globalPopupWindow.closed) {
      console.log('⚠️ Popup already open, focusing existing window')
      globalPopupWindow.focus()
      popupRef.current = globalPopupWindow
      return
    }

    // Close any existing popup in this component's ref
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close()
    }

    // Open in new window
    const width = 500
    const height = 700
    const left = window.screen.width / 2 - width / 2
    const top = window.screen.height / 2 - height / 2

    // Use FIXED window name so only one popup can exist
    const windowName = 'coinbase_onramp_popup'

    // DON'T add cache buster - the URL from Buy Quote API is already unique
    const popup = window.open(
      onrampUrl,
      windowName,
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    )

    popupRef.current = popup
    globalPopupWindow = popup // Store globally

    if (!popup) {
      onError('Popup blocked. Please allow popups for this site.')
      globalInitializing = false
      return
    }

    // Track completion status
    let isCompleted = false

    // Listen for postMessage events from Coinbase popup
    const handleMessage = (event: MessageEvent) => {
      // Verify message is from Coinbase (accept both sandbox and production)
      if (event.origin !== 'https://pay-sandbox.coinbase.com' && event.origin !== 'https://pay.coinbase.com') {
        return
      }

      console.log('📨 Coinbase message received:', event.data)

      // Coinbase sends nested event structure
      const eventName = event.data?.data?.eventName || event.data?.eventName

      // Check for success event
      if (eventName === 'success' || eventName === 'purchase_success') {
        console.log('✅ Coinbase payment successful!')
        isCompleted = true
        if (popup && !popup.closed) {
          popup.close()
        }
        globalOnrampUrl = null // Reset for next use
        globalInitializing = false
        globalPopupWindow = null // Clear global popup reference
        onSuccess('COINBASE_ONRAMP_FUNDED')
      } else if (eventName === 'exit' || eventName === 'close') {
        console.log('❌ User exited Coinbase popup')
        if (!isCompleted) {
          globalOnrampUrl = null // Reset for next use
          globalInitializing = false
          globalPopupWindow = null // Clear global popup reference
          onError('Payment cancelled by user')
        }
      }
    }

    // Add event listener for postMessage
    window.addEventListener('message', handleMessage)

    // Poll for popup close
    const checkPopup = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopup)
        window.removeEventListener('message', handleMessage)

        if (!isCompleted) {
          console.log('❌ Popup closed without completion')
          globalOnrampUrl = null // Reset for next use
          globalInitializing = false
          globalPopupWindow = null // Clear global popup reference
          onError('Payment cancelled - popup was closed')
        }
      }
    }, 500)
  }

  useEffect(() => {
    // Prevent double initialization - check both local AND global flags
    if (hasInitialized.current || globalInitializing) {
      console.log('⚠️ CoinbaseOnramp initialization blocked - already initializing')

      // If another instance is initializing, wait for the URL to be ready
      if (globalOnrampUrl && !popupRef.current) {
        console.log('🔄 Reusing onramp URL from other instance:', globalOnrampUrl)
        openPopupWithUrl(globalOnrampUrl)
        setIsLoading(false)
      } else {
        // Wait for the URL to be populated
        const waitForUrl = setInterval(() => {
          if (globalOnrampUrl && !popupRef.current) {
            console.log('🔄 URL now available, reusing:', globalOnrampUrl)
            openPopupWithUrl(globalOnrampUrl)
            setIsLoading(false)
            clearInterval(waitForUrl)
          }
        }, 50)

        // Timeout after 10 seconds
        setTimeout(() => clearInterval(waitForUrl), 10000)
      }
      return
    }

    hasInitialized.current = true
    globalInitializing = true
    console.log('✅ CoinbaseOnramp initializing')

    const initializeCoinbaseOnramp = async () => {
      try {
        const wallet = wallets[0]
        if (!wallet) {
          onError('No wallet connected')
          globalInitializing = false
          return
        }

        // Get session token from backend
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

        // Get Privy auth token
        const { getAccessToken } = await import('@privy-io/react-auth')
        const privyToken = await getAccessToken()

        if (!privyToken) {
          onError('Authentication required. Please log in.')
          globalInitializing = false
          return
        }

        console.log('📡 Requesting onramp URL from backend via Buy Quote API...')

        // CRITICAL: Use the updated endpoint that uses Buy Quote API
        const response = await fetch(`${API_BASE_URL}/coinbase/onramp/session-token`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${privyToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            walletAddress: wallet.address,
            amount: amount.toString(),
            networks: ['base'],
            assets: ['USDC'],
          }),
        })

        if (!response.ok) {
          const errorData = await response.json()
          console.error('❌ Backend error:', errorData)
          throw new Error(errorData.error || errorData.details || 'Failed to generate onramp URL')
        }

        const data = await response.json()

        if (!data.success || !data.onrampUrl) {
          console.error('❌ Invalid response:', data)
          throw new Error('Invalid onramp URL response from server')
        }

        const onrampUrl = data.onrampUrl

        console.log('✅ Onramp URL received from backend')
        console.log('🔗 URL includes quoteId + sessionToken (from Buy Quote API)')

        // Store URL globally so other instances can reuse it
        globalOnrampUrl = onrampUrl
        console.log('💾 Stored onramp URL globally for reuse')

        // Open popup with the URL
        openPopupWithUrl(onrampUrl)
        setIsLoading(false)
      } catch (error: any) {
        console.error('❌ Failed to initialize onramp:', error)
        setIsLoading(false)
        globalInitializing = false
        globalOnrampUrl = null // Reset on error
        onError(error?.message || 'Failed to initialize Coinbase Onramp')
      }
    }

    initializeCoinbaseOnramp()

    // Cleanup on unmount
    return () => {
      globalInitializing = false
      // Don't close the popup on unmount - let it stay open for the user
      // The popup will be tracked globally and cleaned up when closed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty deps - only run once on mount

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500 mb-4"></div>
            <p className="text-gray-700">Loading Coinbase Onramp...</p>
            <p className="text-sm text-gray-500 mt-2">Generating secure payment URL...</p>
          </div>
        </div>
      </div>
    )
  }

  return null
}
